// AlertTeaser.jsx — 4 regional alert teaser cards for the homepage
// Replaces RRAlertCard in MarketOverviewPage.
// Pulls from :6060/api/show, segments by region, links to /alertas.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const SHOW_API   = '/api/show/api/show';
const VOLUME_API = '/api/show/api/show/volume';

// ── Region logic (same as AlertsPage) ───────────────────────
function getRegion(ticker) {
  if (!ticker) return 'b3';
  if (/^[A-Z0-9]{4}3[2-5]$/.test(ticker)) return 'b3';
  if (!ticker.includes('.')) return 'americas';
  const suf = ticker.split('.').pop().toUpperCase();
  if (suf === 'SA') return 'b3';
  if (['TO','MX','SN'].includes(suf)) return 'americas';
  if (['SR','AE','KW','QA','TA'].includes(suf)) return 'gulf';
  if (['L','DE','PA','AS','ST','OL','SW','BR','VI','HE','IR','MI','AT'].includes(suf)) return 'europe';
  if (['T','KS','TW','AX','NZ','SI','JK','KL','NS','HK','SS','SZ'].includes(suf)) return 'asia';
  return 'other';
}

function fmtPrice(price, currency) {
  if (!price || price <= 0) return null;
  if (!currency || currency === 'BRL') return `R$${price.toFixed(2)}`;
  const noDecimals = ['JPY','KRW','IDR','CLP','TWD'].includes(currency);
  return `${currency} ${noDecimals ? Math.round(price) : price.toFixed(2)}`;
}

function fmtVolBRL(v) {
  if (!v) return null;
  if (v >= 1e9) return `R$${(v/1e9).toFixed(1)}B`;
  if (v >= 1e6) return `R$${(v/1e6).toFixed(1)}M`;
  return `R$${(v/1e3).toFixed(0)}K`;
}

// ── Card config — 4 teaser segments ─────────────────────────
const CARDS = [
  {
    key:     'b3',
    label:   'B3 · Extremos',
    flag:    '🇧🇷',
    accent:  '#f43f5e',
    filter:  (alerts) => alerts.filter(a => getRegion(a.ticker) === 'b3' && a.severity === 'extreme'),
    type:    'extreme',
  },
  {
    key:     'americas',
    label:   'Américas',
    flag:    '🌎',
    accent:  '#6660fe',
    filter:  (alerts) => alerts.filter(a => getRegion(a.ticker) === 'americas'),
    type:    'variacao',
  },
  {
    key:     'asia',
    label:   'Ásia/Pacífico',
    flag:    '🌏',
    accent:  '#22d3ee',
    filter:  (alerts, vols) => {
      const a = alerts.filter(x => getRegion(x.ticker) === 'asia').slice(0, 3);
      const v = vols.filter(x => getRegion(x.ticker) === 'asia').slice(0, 2);
      return [...a, ...v.filter(x => !a.find(y => y.ticker === x.ticker))];
    },
    type:    'mixed',
  },
  {
    key:     'europe_gulf',
    label:   'Europa & Golfo',
    flag:    '🌍',
    accent:  '#D4AF37',
    filter:  (alerts) => alerts.filter(a => ['europe','gulf'].includes(getRegion(a.ticker))),
    type:    'variacao',
  },
];

// ── Mini alert row ───────────────────────────────────────────
function MiniAlert({ alert, accent }) {
  const navigate = useNavigate();
  const isUp = alert.direction === 'up';
  const chgColor = alert.severity === 'extreme' ? '#f43f5e' : isUp ? '#4ade80' : '#f87171';
  const price = fmtPrice(alert.price_brl > 0 ? alert.price_brl : alert.price, alert.price_brl > 0 ? 'BRL' : alert.currency);

  return (
    <div
      onClick={() => navigate(`/asset/stock/${alert.ticker}`)}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '7px 10px', borderRadius: 5, cursor: 'pointer',
        background: 'rgba(255,255,255,0.03)',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
    >
      <div style={{ minWidth: 0 }}>
        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, fontWeight: 800, color: '#D4AF37' }}>{alert.ticker}</span>
        {alert.severity === 'extreme' && (
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 7, fontWeight: 700, color: '#f43f5e', marginLeft: 5, letterSpacing: '0.08em' }}>⚡</span>
        )}
        <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>{alert.name}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 12, fontWeight: 700, color: chgColor }}>
          {isUp ? '▲' : '▼'} {Math.abs(alert.change).toFixed(2)}%
        </div>
        {price && <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>{price}</div>}
      </div>
    </div>
  );
}

// ── Single teaser card ───────────────────────────────────────
function TeaserCard({ config, alerts, volumeMovers }) {
  const navigate = useNavigate();
  const items = config.filter(alerts, volumeMovers).slice(0, 5);
  const extremeCount = alerts.filter(a => getRegion(a.ticker) === config.key || (config.key === 'europe_gulf' && ['europe','gulf'].includes(getRegion(a.ticker)))).filter(a => a.severity === 'extreme').length;

  return (
    <div style={{
      background: '#0d0b18',
      border: `1px solid rgba(255,255,255,0.07)`,
      borderTop: `2px solid ${config.accent}`,
      borderRadius: 10,
      padding: '14px 14px 12px',
      display: 'flex', flexDirection: 'column', gap: 0,
      flex: '1 1 220px', minWidth: 0,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14 }}>{config.flag}</span>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.1em' }}>
            {config.label.toUpperCase()}
          </span>
        </div>
        {extremeCount > 0 && (
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 8, fontWeight: 700, color: '#f43f5e', border: '1px solid #f43f5e44', padding: '1px 6px', borderRadius: 2, letterSpacing: '0.08em' }}>
            {extremeCount} ⚡
          </span>
        )}
      </div>

      {/* Alert rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
        {items.length === 0 ? (
          <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.2)', padding: '12px 0', textAlign: 'center' }}>Sem alertas ativos</div>
        ) : (
          items.map((a, i) => <MiniAlert key={`${a.ticker}-${i}`} alert={a} accent={config.accent} />)
        )}
      </div>

      {/* Footer CTA */}
      <button
        onClick={() => navigate(`/alertas?region=${config.key}`)}
        style={{
          marginTop: 10, width: '100%', padding: '7px 0',
          fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700,
          letterSpacing: '0.12em', color: config.accent,
          background: `${config.accent}10`, border: `1px solid ${config.accent}30`,
          borderRadius: 5, cursor: 'pointer', transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${config.accent}20`; e.currentTarget.style.borderColor = `${config.accent}60`; }}
        onMouseLeave={e => { e.currentTarget.style.background = `${config.accent}10`; e.currentTarget.style.borderColor = `${config.accent}30`; }}
      >
        VER TODOS ↗
      </button>
    </div>
  );
}

// ── Main export ──────────────────────────────────────────────
export function AlertTeaser() {
  const [alerts, setAlerts]           = useState([]);
  const [volumeMovers, setVolMovers]  = useState([]);
  const [summary, setSummary]         = useState({});
  const [loading, setLoading]         = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const fetch_ = async () => {
      try {
        const [showRes, volRes] = await Promise.all([fetch(SHOW_API), fetch(VOLUME_API)]);
        const show = await showRes.json();
        const vol  = await volRes.json();
        if (!alive) return;
        setAlerts(show.alerts || []);
        setVolMovers(vol.volume_movers || show.volume_movers || []);
        setSummary(show.summary || {});
        setLoading(false);
      } catch { setLoading(false); }
    };
    fetch_();
    const id = setInterval(fetch_, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div style={{ width: '100%' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 3, height: 24, background: 'linear-gradient(180deg,#f43f5e,rgba(244,63,94,0))', borderRadius: 2 }} />
          <span style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: '1.4rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>Alertas Globais</span>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, color: '#6660fe', border: '1px solid #6660fe44', padding: '2px 7px', borderRadius: 2, letterSpacing: '0.12em' }}>DINO</span>
          {summary.total_alerts > 0 && (
            <span style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
              {summary.total_alerts} ativos · {summary.extreme_count || 0} extremos
            </span>
          )}
        </div>
        <button
          onClick={() => navigate('/alertas')}
          style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.4)', background: 'none', border: '1px solid rgba(255,255,255,0.1)', padding: '5px 12px', borderRadius: 4, cursor: 'pointer', transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
        >
          PAINEL COMPLETO ↗
        </button>
      </div>

      {loading ? (
        <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.15em', padding: '2rem 0', textAlign: 'center' }}>CARREGANDO ALERTAS…</div>
      ) : (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {CARDS.map(c => (
            <TeaserCard key={c.key} config={c} alerts={alerts} volumeMovers={volumeMovers} />
          ))}
        </div>
      )}
    </div>
  );
}

export default AlertTeaser;
